/**
 * Which in-flight fetcher POSTs count as "the shared basket is changing"
 * (COM-48).
 *
 * Extracted from `basket.tsx` so it can be asserted directly: `BasketProvider`
 * suppresses its stale basket snapshot off this predicate, and the failure mode
 * when a route is missing here is silent — the minicart simply keeps showing
 * pre-mutation contents. A `.ts` leaf (not the `.tsx` route) so a test can
 * import it without the cart component tree.
 */
import { RESOURCE_URL as BASKET_RESOURCE_URL } from "./schemas/basket";
import { REORDER_RESOURCE_URL } from "./schemas/reorder";

/**
 * Resource routes whose POST mutates the shared basket.
 *
 * Reorder posts to its OWN route rather than the basket route, so matching
 * only the basket URL leaves the minicart stale after a reorder — this was
 * recorded as a contract dependency on COM-48 in
 * `order-migration-plan/index.md`. The ET-30 telehealth plan-selection add
 * posts to its own route for the same reason (a plain add + custom-attr PATCH
 * the basket route's schema deliberately rejects), so it belongs here too.
 * Any future basket-mutating resource route must be added here; the provider
 * cannot infer it.
 *
 * This is NOT a global registry of "things that touch the basket" — three other
 * gates hardcode `/resources/basket` on purpose and are left alone:
 * `dispatcher-should-revalidate.ts` (catch-all revalidation; reorder is
 * submitted from `/orders/:orderNo`, not a dispatcher route), and the
 * added-to-basket flyout in `components/added-to-basket-flyout.tsx` /
 * `product-quick-view.tsx` (a reorder redirects to the cart, so it must not
 * raise the flyout). Widen those only with a reason.
 */
export const MUTATING_RESOURCE_URLS = [
  BASKET_RESOURCE_URL,
  REORDER_RESOURCE_URL,
  // ET-30: kept as a literal rather than imported from
  // `./telehealth-plan-basket` so that server action module is not pulled into
  // the client-mounted `BasketProvider`'s bundle. Registered in `routes.ts`.
  "/resources/telehealth-plan-basket",
] as const;

/** Narrow shape so callers can pass a React Router fetcher directly. */
type FetcherLike = {
  formMethod?: string;
  formAction?: string;
};

export function isBasketMutationFetcher(fetcher: FetcherLike): boolean {
  if (fetcher.formMethod?.toUpperCase() !== "POST") return false;
  const action = fetcher.formAction ?? "";
  return MUTATING_RESOURCE_URLS.some(url => action.includes(url));
}
